import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'react'
import styles from './ParagraphReaderView.module.scss'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  GET_PARAGRAPHS_QUERY,
  GET_PARAGRAPH_WITH_CONTENT_QUERY,
  DELETE_PARAGRAPH_MUTATION,
  ADD_PARAGRAPH_WORD_LOOKUP_MUTATION,
  RECORD_PRACTICE_MUTATION
} from '../query/gqlQueries'
import UserContext from '../context/UserContext'
import { DialogContext } from '../context/DialogContext'
import languages from '../languages'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { posColor } from '../dialog/AddParagraphDialog'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import StyleIcon from '@mui/icons-material/Style'
import MicIcon from '@mui/icons-material/Mic'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ParagraphPracticeView from './ParagraphPracticeView'

// Light / dark, shared with the lesson, Special Mode and home views through the
// same key, so one choice carries across all of them.
const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }
// Reading one sentence at a time is the default: a whole paragraph of coloured,
// tappable words on screen at once is a lot to take in.
const FOCUS_KEY = 'paragraphOneAtATime'
const readFocus = () => { try { return localStorage.getItem(FOCUS_KEY) !== '0' } catch { return true } }

// A word counts as "historically weak" — and is pre-flagged red in the reader
// across sessions — if EITHER of these is true:
//   1. Accuracy < 70% (you've gotten it wrong more than right)
//   2. Not practiced in 30+ days (forgetting curve resurfaces old material)
// Words never practiced (timesPracticed === 0) are NOT pre-flagged — they
// remain neutral until you mark them missed or get them wrong in flashcards.
const ACCURACY_THRESHOLD = 0.7
const STALE_DAYS = 30
const isHistoricallyWeak = (timesPracticed, timesCorrect, lastPracticedAt) => {
  if (!timesPracticed || timesPracticed === 0) return false
  const accuracy = (timesCorrect || 0) / timesPracticed
  if (accuracy < ACCURACY_THRESHOLD) return true
  if (lastPracticedAt) {
    const daysSince = (Date.now() - new Date(lastPracticedAt).getTime()) / 86_400_000
    if (Number.isFinite(daysSince) && daysSince > STALE_DAYS) return true
  }
  return false
}

const POS_LEGEND = [
  ['noun', 'Noun'],
  ['verb', 'Verb'],
  ['adjective', 'Adjective'],
  ['adverb', 'Adverb'],
  ['pronoun', 'Pronoun'],
  ['particle', 'Particle'],
  ['conjunction', 'Conjunction'],
  ['interjection', 'Interjection'],
  ['preposition', 'Preposition'],
  ['determiner', 'Determiner'],
  ['numeral', 'Numeral'],
  ['auxiliary', 'Auxiliary']
]

export default function ParagraphReaderView({ setIsVisible, voiceSpeed, onPracticeParagraphContent }) {
  const { username } = useContext(UserContext)
  const { openDialog } = useContext(DialogContext)
  const [selectedId, setSelectedId] = useState(null)
  const [hoverData, setHoverData] = useState(null) // { token, x, y, translation, pos, loading }
  // Off by default now: the reader should be calm to read, and the colours are
  // there when you want to study structure.
  const [showPOSColors, setShowPOSColors] = useState(false)
  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ }
    return next
  })
  const [oneAtATime, setOneAtATime] = useState(readFocus)
  const [sentIdx, setSentIdx] = useState(0)
  const chooseFocus = (v) => { setOneAtATime(v); try { localStorage.setItem(FOCUS_KEY, v ? '1' : '0') } catch { /* private mode */ } }
  const [wordCache, setWordCache] = useState({}) // surface(lowercased) → translation
  const [missedCounts, setMissedCounts] = useState({}) // surface(lower) → effective miss count (history + session)
  const [flashingWords, setFlashingWords] = useState(new Set()) // surface(lower) for active flash anim
  const [focusedPOS, setFocusedPOS] = useState(null) // when set, only words of this POS stay bright
  const [isPracticeMode, setIsPracticeMode] = useState(false) // toggle Spotify-style read-aloud practice

  // Tracks which paragraph id we've already seeded missedCounts for, so
  // refetches (triggered by adding word lookups, etc.) don't wipe out
  // in-session click increments. Reset on handleBack.
  const seededForRef = useRef(null)

  const { data: listData, loading: listLoading, refetch: refetchList } = useQuery(GET_PARAGRAPHS_QUERY, {
    variables: { username },
    skip: !username
  })

  const [fetchContent, { data: contentData, loading: contentLoading, refetch: refetchContent }] =
    useLazyQuery(GET_PARAGRAPH_WITH_CONTENT_QUERY, { fetchPolicy: 'cache-and-network' })

  const [deleteParagraph] = useMutation(DELETE_PARAGRAPH_MUTATION)
  const [addWordLookup] = useMutation(ADD_PARAGRAPH_WORD_LOOKUP_MUTATION)
  const [recordPractice] = useMutation(RECORD_PRACTICE_MUTATION)

  const paragraphs = listData?.getParagraphs || []
  const content = contentData?.getParagraphWithContent

  // Parse posMap + word cache from the loaded paragraph
  const { posMap, wordTranslations, wordIdByText } = useMemo(() => {
    if (!content) return { posMap: {}, wordTranslations: {}, wordIdByText: {} }
    let pm = {}
    try {
      const raw = content.paragraph.properties.posMap
      if (raw) pm = JSON.parse(raw)
    } catch (e) {
      console.warn('Failed to parse posMap', e)
    }
    const wt = {}
    const ids = {}
    content.words.forEach(pair => {
      const key = pair.source.text.toLowerCase()
      if (pair.target?.text) wt[key] = pair.target.text
      ids[key] = pair.source.id
    })
    return { posMap: pm, wordTranslations: wt, wordIdByText: ids }
  }, [content])

  const sentences = content?.sentences || []
  const paragraph = content?.paragraph
  const sourceLang = paragraph?.properties?.lang || 'eng'

  // One-at-a-time reading: which sentence is on screen, and the arrow keys.
  const atSent = Math.min(sentIdx, Math.max(0, sentences.length - 1))
  const stepSent = useCallback((d) => setSentIdx(i => Math.max(0, Math.min(sentences.length - 1, i + d))), [sentences.length])
  useEffect(() => { setSentIdx(0) }, [selectedId])
  useEffect(() => {
    if (!selectedId || !oneAtATime || isPracticeMode) return
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return
      if (e.key === 'ArrowRight') stepSent(1)
      else if (e.key === 'ArrowLeft') stepSent(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, oneAtATime, isPracticeMode, stepSent])

  const handleSelect = (identity) => {
    setSelectedId(identity)
    fetchContent({ variables: { identity, username } })
  }

  const handleBack = () => {
    setSelectedId(null)
    setHoverData(null)
    setWordCache({})
    setMissedCounts({})
    setFlashingWords(new Set())
    setFocusedPOS(null)
    setIsPracticeMode(false)
    seededForRef.current = null
  }

  // Seed missedCounts from server practice history the first time a paragraph
  // loads. Runs once per paragraph — subsequent refetches (e.g. after a word
  // lookup) don't reset session-incremented counts.
  useEffect(() => {
    if (!content?.words || !content?.paragraph) return
    const pid = content.paragraph.identity
    if (seededForRef.current === pid) return
    seededForRef.current = pid

    const seeded = {}
    content.words.forEach(pair => {
      if (!pair.source) return
      const { text, timesPracticed, timesCorrect, lastPracticedAt } = pair.source
      if (isHistoricallyWeak(timesPracticed, timesCorrect, lastPracticedAt)) {
        const misses = (timesPracticed || 0) - (timesCorrect || 0)
        seeded[text.toLowerCase()] = misses > 0 ? misses : 1
      }
    })
    setMissedCounts(seeded)
  }, [content])

  const handleDelete = async (identity, e) => {
    e.stopPropagation()
    if (!window.confirm('Delete this paragraph and all its sentences/words?')) return
    await deleteParagraph({ variables: { identity } })
    if (selectedId === identity) setSelectedId(null)
    refetchList()
  }

  // Tokenize a sentence into renderable spans.
  // We keep non-letter runs (spaces, punctuation) as their own tokens so we
  // can preserve original spacing when rendering.
  const tokenize = useCallback((text) => {
    if (!text) return []
    // Matches: runs of letters/numbers (incl. CJK/Hangul/Arabic etc.) OR runs of everything else
    const re = /(\p{L}[\p{L}\p{N}'’\-]*|\p{N}+|[^\p{L}\p{N}]+)/gu
    const out = []
    let m
    while ((m = re.exec(text)) !== null) {
      const tok = m[1]
      const isWord = /^[\p{L}\p{N}]/u.test(tok)
      out.push({ text: tok, isWord })
    }
    return out
  }, [])

  const resolveTranslation = useCallback(async (surface) => {
    const key = surface.toLowerCase()
    if (wordCache[key]) return wordCache[key]
    if (wordTranslations[key]) return wordTranslations[key]

    // Not cached — ask Ollama for a one-word translation
    try {
      const serverUrl = process.env.REACT_APP_OLLAMA_URI || 'http://localhost:11434'
      const langName = languages[sourceLang] || sourceLang
      const prompt = `Translate the following ${langName} word into English. Output ONLY the English translation, no quotes, no explanation, no labels. If the word is a grammatical particle that doesn't translate, output its function (e.g. "topic marker"). Word: ${surface}`
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_OLLAMA_KEY || ''
        },
        body: JSON.stringify({
          model: 'qwen2.5:7b',
          prompt,
          stream: false,
          options: { temperature: 0.1, num_predict: 60 }
        })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      let t = (data.response || '').trim()
        .replace(/^["'`“”‘’]+|["'`“”‘’]+$/g, '')
        .split(/\r?\n/)[0]
        .trim()
      if (!t) t = '(no translation)'
      setWordCache(prev => ({ ...prev, [key]: t }))

      // Persist on the backend so future loads of this paragraph have it cached.
      if (paragraph?.identity && username) {
        addWordLookup({
          variables: {
            username,
            paragraphId: paragraph.identity,
            word: surface,
            translation: t,
            lang: sourceLang
          }
        }).catch(err => console.warn('addParagraphWordLookup failed', err))
      }
      return t
    } catch (err) {
      console.error(err)
      return '(lookup failed)'
    }
  }, [wordCache, wordTranslations, sourceLang, paragraph, username, addWordLookup])

  const handleWordEnter = useCallback(async (e, surface) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const key = surface.toLowerCase()
    const pos = posMap[key] || 'other'
    const cached = wordCache[key] || wordTranslations[key]

    setHoverData({
      token: surface,
      x: rect.left + rect.width / 2,
      y: rect.top,
      pos,
      translation: cached || null,
      loading: !cached
    })

    if (!cached) {
      const t = await resolveTranslation(surface)
      setHoverData(prev => (prev && prev.token === surface
        ? { ...prev, translation: t, loading: false }
        : prev))
    }
  }, [posMap, wordCache, wordTranslations, resolveTranslation])

  const handleWordLeave = useCallback(() => {
    setHoverData(null)
  }, [])

  const handleWordClick = useCallback(async (e, surface) => {
    e.stopPropagation()
    const key = surface.toLowerCase()

    // ── Visual feedback ──────────────────────────────────────────────
    // 1. Persistent "you missed this" indicator (stays for the session)
    setMissedCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }))
    // 2. Brief flash so the user knows the click registered right now
    setFlashingWords(prev => {
      const next = new Set(prev)
      next.add(key)
      return next
    })
    setTimeout(() => {
      setFlashingWords(prev => {
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }, 700)
    // 3. If the tooltip is currently showing this word, refresh its miss count
    setHoverData(prev => (prev && prev.token.toLowerCase() === key
      ? { ...prev, missCount: (missedCounts[key] || 0) + 1 }
      : prev))

    // ── Persist to backend ───────────────────────────────────────────
    // Mark as practiced-incorrect (i.e. user needed to look this up).
    // If the Word node doesn't exist yet, force a lookup first so we can target it.
    let wordId = wordIdByText[key]
    if (!wordId) {
      await resolveTranslation(surface)
      // refetch paragraph content to pick up the newly-created Word node
      try {
        const { data } = await refetchContent({ identity: paragraph.identity, username })
        const fresh = data?.getParagraphWithContent?.words || []
        const found = fresh.find(p => p.source.text.toLowerCase() === key)
        wordId = found?.source?.id
      } catch (err) {
        console.warn('refetchContent failed', err)
      }
    }
    if (wordId && username) {
      recordPractice({
        variables: { itemId: wordId, itemType: 'Word', isCorrect: false }
      }).catch(err => console.warn('recordPractice failed', err))
    }
  }, [wordIdByText, paragraph, username, resolveTranslation, refetchContent, recordPractice, missedCounts])

  // Build flashcards from this paragraph's words. Sort missed-words first
  // (descending by miss count), then alphabetical for stable order. The
  // FlashcardView shuffles for game modes but uses the first card as the
  // initial display — so the most-missed word leads.
  const buildFlashcards = useCallback((missedOnly) => {
    if (!content?.words) return []
    const cards = content.words
      .filter(pair => pair.source && pair.target)
      .map(pair => {
        const key = pair.source.text.toLowerCase()
        const missCount = missedCounts[key] || 0
        return {
          sourceId: pair.source.id,
          source: pair.source.text,
          sourceLanguage: pair.source.lang,
          sourceImageUrl: pair.source.imageUrl || null,
          sourceImageDescription: pair.source.imageDescription || null,
          translations: [{
            target: pair.target.text,
            targetLanguage: pair.target.lang || 'eng',
            targetId: pair.target.id,
          }],
          _missCount: missCount
        }
      })

    const filtered = missedOnly ? cards.filter(c => c._missCount > 0) : cards
    return filtered.sort((a, b) => {
      if (b._missCount !== a._missCount) return b._missCount - a._missCount
      return a.source.localeCompare(b.source)
    })
  }, [content, missedCounts])

  const missedWordCount = Object.keys(missedCounts).length

  if (!username) {
    return (
      <div className={`${styles.view} ${styles[theme]}`}>
        <div className={styles.status}>Sign in to view paragraphs.</div>
      </div>
    )
  }

  // ───────────────── PRACTICE (READ-ALOUD) MODE ─────────────────
  // The practice view owns its own fixed-position fullscreen layer, so we
  // render it as a direct return (no wrapper). onClose returns to the reader;
  // onExit closes the whole paragraph view back to the list/Hud.
  if (selectedId && content && isPracticeMode) {
    return (
      <ParagraphPracticeView
        paragraph={paragraph}
        sentences={sentences}
        words={content.words}
        onClose={() => setIsPracticeMode(false)}
        onExit={() => { setIsPracticeMode(false); setIsVisible(false) }}
      />
    )
  }

  // ───────────────────── DETAIL/READER VIEW ─────────────────────
  if (selectedId && content) {
    return (
      <div className={`${styles.view} ${styles[theme]}`}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}>
          <CloseIcon />
        </button>

        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={handleBack}>
              <ArrowBackIcon fontSize="small" /> Paragraphs
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{languages[sourceLang] || sourceLang}</div>
              <h1 className={styles.title}>
                {paragraph.properties.title || 'Untitled Paragraph'}
              </h1>
            </div>
            <div className={styles.toolbar}>
              <span className={styles.segmented}>
                <button
                  className={`${styles.segBtn} ${oneAtATime ? styles.segBtnOn : ''}`}
                  onClick={() => chooseFocus(true)}
                  title="Read one sentence at a time (← → to move)">one at a time</button>
                <button
                  className={`${styles.segBtn} ${!oneAtATime ? styles.segBtnOn : ''}`}
                  onClick={() => chooseFocus(false)}
                  title="Show the whole paragraph">whole text</button>
              </span>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={showPOSColors}
                  onChange={(e) => setShowPOSColors(e.target.checked)}
                /> POS colors
              </label>
              <button
                className={styles.iconBtn}
                title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                onClick={toggleTheme}>
                {theme === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </button>
              <button
                className={styles.iconBtn}
                title="Reload"
                onClick={() => refetchContent({ identity: selectedId, username })}>
                <RefreshIcon fontSize="small" />
              </button>
            </div>
          </header>

          {((onPracticeParagraphContent && content?.words?.length > 0) || sentences.length > 0) && (
            <div className={styles.actionBar}>
              {onPracticeParagraphContent && missedWordCount > 0 && (
                <button
                  className={`${styles.iconBtn} ${styles.primaryBtn}`}
                  onClick={() => onPracticeParagraphContent(buildFlashcards(true))}
                  title={`Practice the ${missedWordCount} word${missedWordCount === 1 ? '' : 's'} you marked as missed`}>
                  <StyleIcon fontSize="small" /> Practice missed ({missedWordCount})
                </button>
              )}
              {onPracticeParagraphContent && content?.words?.length > 0 && (
                <button
                  className={styles.iconBtn}
                  onClick={() => onPracticeParagraphContent(buildFlashcards(false))}
                  title="Practice every word in this paragraph (missed-first, then alphabetical)">
                  <StyleIcon fontSize="small" /> Practice all ({content.words.length})
                </button>
              )}
              {sentences.length > 0 && (
                <button
                  className={styles.iconBtn}
                  onClick={() => setIsPracticeMode(true)}
                  title="Read each sentence aloud — your microphone listens and marks missed words">
                  <MicIcon fontSize="small" /> Read aloud
                </button>
              )}
            </div>
          )}

          {showPOSColors && (
            <div className={styles.legend}>
              {POS_LEGEND.map(([key, label]) => {
                const isActive = focusedPOS === key
                const color = posColor(key, theme)
                return (
                  <button
                    key={key}
                    type="button"
                    className={[styles.legendChip, isActive ? styles.legendChipActive : ''].join(' ')}
                    style={{
                      borderColor: color,
                      background: isActive ? `${color}33` : undefined,
                      color: isActive ? '#fff' : undefined
                    }}
                    onClick={() => setFocusedPOS(prev => prev === key ? null : key)}
                    title={isActive ? `Stop focusing on ${label.toLowerCase()}s` : `Focus on ${label.toLowerCase()}s only`}>
                    <span className={styles.legendDot} style={{ background: color }} />
                    {label}
                  </button>
                )
              })}
              {focusedPOS && (
                <button
                  type="button"
                  className={styles.legendClear}
                  onClick={() => setFocusedPOS(null)}
                  title="Clear focus filter">
                  Clear focus
                </button>
              )}
            </div>
          )}

          {/* One sentence at a time by default; the arrow keys move too. */}
          {oneAtATime && sentences.length > 1 && (
            <div className={styles.stepRow}>
              <button className={styles.stepBtn} onClick={() => stepSent(-1)} disabled={atSent === 0} title="Previous sentence (←)">
                <NavigateBeforeIcon fontSize="small" /> Prev
              </button>
              <span className={styles.stepCount}>{atSent + 1} / {sentences.length}</span>
              <button className={styles.stepBtn} onClick={() => stepSent(1)} disabled={atSent >= sentences.length - 1} title="Next sentence (→)">
                Next <NavigateNextIcon fontSize="small" />
              </button>
            </div>
          )}

          <div className={styles.reader}>
            {sentences.length === 0 ? (
              <div className={styles.fallback}>{paragraph.properties.text}</div>
            ) : (
              (oneAtATime ? sentences.slice(atSent, atSent + 1) : sentences).map((pair, sidx) => (
                <div key={pair.source.id} className={styles.sentenceBlock}>
                  <div className={styles.sentence}>
                    <button
                      className={styles.speakBtn}
                      onClick={(e) => { e.stopPropagation(); speak(pair.source.text, voices[pair.source.lang] || voices['eng'], voiceSpeed) }}
                      title="Read sentence aloud">
                      <VolumeUpIcon fontSize="small" />
                    </button>
                    <span className={styles.sentenceText}>
                      {tokenize(pair.source.text).map((tok, i) => {
                        if (!tok.isWord) {
                          // Dim punctuation/whitespace alongside non-matching words
                          // so the whole sentence fades together except focused tokens.
                          return (
                            <span key={i} className={focusedPOS ? styles.tokenDimmed : ''}>{tok.text}</span>
                          )
                        }
                        const key = tok.text.toLowerCase()
                        const pos = posMap[key] || 'other'
                        const isDimmed = focusedPOS && pos !== focusedPOS
                        const color = showPOSColors && !isDimmed ? posColor(pos, theme) : 'transparent'
                        const classes = [styles.word]
                        if (missedCounts[key]) classes.push(styles.wordMissed)
                        if (flashingWords.has(key)) classes.push(styles.wordFlashing)
                        if (isDimmed) classes.push(styles.tokenDimmed)
                        return (
                          <span
                            key={i}
                            className={classes.join(' ')}
                            style={{ borderBottomColor: color }}
                            onMouseEnter={(e) => handleWordEnter(e, tok.text)}
                            onMouseLeave={handleWordLeave}
                            onClick={(e) => handleWordClick(e, tok.text)}>
                            {tok.text}
                          </span>
                        )
                      })}
                    </span>
                  </div>
                  {pair.target?.text && (
                    <div className={styles.translation}>{pair.target.text}</div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {hoverData && (
          <div
            className={styles.tooltip}
            style={{ left: hoverData.x, top: hoverData.y }}>
            <div className={styles.tooltipWord}>{hoverData.token}</div>
            <div className={styles.tooltipPos} style={{ color: posColor(hoverData.pos, theme) }}>
              {hoverData.pos}
            </div>
            <div className={styles.tooltipTranslation}>
              {hoverData.loading ? 'Translating…' : (hoverData.translation || '(no translation)')}
            </div>
            {missedCounts[hoverData.token.toLowerCase()] > 0 && (
              <div className={styles.tooltipMissed}>
                Marked weak ({missedCounts[hoverData.token.toLowerCase()]}× total)
              </div>
            )}
            <div className={styles.tooltipHint}>click word = mark as missed</div>
          </div>
        )}
      </div>
    )
  }

  // ───────────────────── LIST VIEW ─────────────────────
  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}>
        <CloseIcon />
      </button>

      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Reading Practice</div>
            <h1 className={styles.title}>Paragraphs</h1>
          </div>
          <div className={styles.toolbar}>
            <button
              className={`${styles.iconBtn} ${styles.primaryBtn}`}
              onClick={() => openDialog('dialogAddParagraph')}>
              <AddIcon fontSize="small" /> Add Paragraph
            </button>
            <button className={styles.iconBtn} onClick={() => refetchList()} title="Reload">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {listLoading && <div className={styles.status}>Loading paragraphs…</div>}

        {!listLoading && paragraphs.length === 0 && (
          <div className={styles.emptyState}>
            <p>No paragraphs yet. Add your first one to start reading practice.</p>
            <button
              className={`${styles.iconBtn} ${styles.primaryBtn}`}
              onClick={() => openDialog('dialogAddParagraph')}>
              <AddIcon fontSize="small" /> Add Paragraph
            </button>
          </div>
        )}

        <div className={styles.list}>
          {paragraphs.map(p => (
            <div
              key={p.identity}
              className={styles.listItem}
              onClick={() => handleSelect(p.identity)}>
              <div className={styles.listMeta}>
                <span className={styles.listLang}>
                  {languages[p.properties.lang] || p.properties.lang}
                </span>
              </div>
              <div className={styles.listTitle}>
                {p.properties.title || 'Untitled Paragraph'}
              </div>
              <div className={styles.listPreview}>
                {(p.properties.text || '').slice(0, 180)}
                {(p.properties.text?.length || 0) > 180 ? '…' : ''}
              </div>
              <button
                className={styles.deleteBtn}
                onClick={(e) => handleDelete(p.identity, e)}
                title="Delete paragraph">
                <DeleteOutlineIcon fontSize="small" />
              </button>
            </div>
          ))}
        </div>

        {contentLoading && <div className={styles.status}>Loading paragraph…</div>}
      </div>
    </div>
  )
}
